import { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import QrCode2RoundedIcon from "@mui/icons-material/QrCode2Rounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import { Button } from "../components/Buttons";
import {
  adminListQrCodes,
  adminCreateQrCode,
  adminUpdateQrCode,
  adminDeleteQrCode,
  adminListPosMachines,
  adminCreatePosMachine,
  adminUpdatePosMachine,
  adminDeletePosMachine,
} from "../api";

const ACCEPTED = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_UPLOAD_SIZE = 5 * 1024 * 1024;

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/* ================================================================== */
/*  QR CODES                                                           */
/* ================================================================== */

function QrCodesTab({ notify }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // null | {} | row
  const [form, setForm] = useState({});
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    adminListQrCodes()
      .then(({ data }) => setRows(data.qrCodes || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setForm({ label: "", upiId: "", note: "" });
    setFile(null);
    setDialog({});
  };

  const openEdit = (row) => {
    setForm({
      label: row.label,
      upiId: row.upi_id || "",
      note: row.note || "",
    });
    setFile(null);
    setDialog(row);
  };

  const pickFile = async (e) => {
    const picked = e.target.files?.[0];
    e.target.value = "";
    if (!picked) return;
    if (!ACCEPTED.includes(picked.type))
      return notify("error", "Upload a JPG, PNG or WEBP image");
    if (picked.size > MAX_UPLOAD_SIZE)
      return notify("error", "Image must be under 5 MB");
    setFile({ name: picked.name, dataUrl: await readFileAsDataUrl(picked) });
  };

  const save = async () => {
    setSaving(true);
    try {
      const isNew = dialog && !dialog.id;
      if (isNew) {
        if (!file) {
          setSaving(false);
          return notify("error", "Upload the QR image");
        }
        await adminCreateQrCode({ ...form, dataUrl: file.dataUrl });
      } else {
        await adminUpdateQrCode(dialog.id, {
          ...form,
          dataUrl: file?.dataUrl,
        });
      }
      setDialog(null);
      notify("success", isNew ? "QR code added." : "QR code updated.");
      load();
    } catch (e) {
      notify("error", e.response?.data?.error || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await adminUpdateQrCode(row.id, { isActive: !row.is_active });
      load();
    } catch (e) {
      notify("error", e.response?.data?.error || "Could not update");
    }
  };

  const doDelete = async () => {
    try {
      const { data } = await adminDeleteQrCode(confirmDelete.id);
      notify(
        "success",
        data.deactivated
          ? "This QR has collections against it, so it was deactivated instead of deleted."
          : "QR code removed.",
      );
      setConfirmDelete(null);
      load();
    } catch (e) {
      notify("error", e.response?.data?.error || "Could not remove");
    }
  };

  if (loading)
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" color="text.secondary">
          Users see every <b>active</b> QR on their POS &amp; QR page. Deactivate
          one to rotate it out without losing its history.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddRoundedIcon />}
          onClick={openNew}
          sx={{ borderRadius: 2, whiteSpace: "nowrap" }}
        >
          Add QR
        </Button>
      </Stack>

      {rows.length === 0 ? (
        <Card sx={{ borderRadius: 3 }}>
          <Stack alignItems="center" sx={{ py: 6 }}>
            <QrCode2RoundedIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
            <Typography color="text.secondary">
              No QR codes yet — add one for users to pay into
            </Typography>
          </Stack>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {rows.map((r) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={r.id}>
              <Card
                sx={{
                  borderRadius: 3,
                  height: "100%",
                  opacity: r.is_active ? 1 : 0.55,
                  border: "1px solid",
                  borderColor: r.is_active ? "success.light" : "divider",
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Stack direction="row" alignItems="center" gap={1} mb={1}>
                    <Typography fontWeight={700} fontSize={14} noWrap flex={1}>
                      {r.label}
                    </Typography>
                    <Tooltip title={r.is_active ? "Active" : "Inactive"}>
                      <Switch
                        size="small"
                        checked={!!r.is_active}
                        onChange={() => toggleActive(r)}
                      />
                    </Tooltip>
                  </Stack>

                  <Box
                    sx={{
                      bgcolor: "#fff",
                      borderRadius: 2,
                      p: 1,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    {r.imageUrl ? (
                      <Box
                        component="img"
                        src={r.imageUrl}
                        alt={r.label}
                        sx={{
                          width: "100%",
                          aspectRatio: "1 / 1",
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                    ) : (
                      <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={{ aspectRatio: "1 / 1" }}
                      >
                        <QrCode2RoundedIcon
                          sx={{ fontSize: 40, color: "text.disabled" }}
                        />
                      </Stack>
                    )}
                  </Box>

                  {r.upi_id && (
                    <Typography
                      variant="caption"
                      sx={{ fontFamily: "monospace", display: "block", mt: 1 }}
                      noWrap
                    >
                      {r.upi_id}
                    </Typography>
                  )}

                  <Stack direction="row" justifyContent="flex-end" gap={0.5} mt={1}>
                    <IconButton size="small" onClick={() => openEdit(r)}>
                      <EditRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setConfirmDelete(r)}
                    >
                      <DeleteRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add / edit */}
      <Dialog open={!!dialog} onClose={() => !saving && setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>
          {dialog?.id ? "Edit QR code" : "Add QR code"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Label"
              placeholder="e.g. HDFC merchant QR"
              value={form.label || ""}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
            />
            <TextField
              fullWidth
              size="small"
              label="UPI ID (optional)"
              placeholder="merchant@okhdfcbank"
              value={form.upiId || ""}
              onChange={(e) => setForm({ ...form, upiId: e.target.value })}
            />
            <TextField
              fullWidth
              size="small"
              label="Note for users (optional)"
              multiline
              minRows={2}
              value={form.note || ""}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
            />

            <Button
              component="label"
              variant="outlined"
              fullWidth
              startIcon={<UploadFileRoundedIcon />}
              sx={{
                height: "auto",
                py: 2,
                borderRadius: 2,
                borderStyle: "dashed",
                textTransform: "none",
                fontSize: 15,
              }}
            >
              {file
                ? file.name
                : dialog?.id
                  ? "Replace QR image (optional)"
                  : "Upload QR image"}
              <input hidden type="file" accept={ACCEPTED.join(",")} onChange={pickFile} />
            </Button>

            {file && (
              <Box
                component="img"
                src={file.dataUrl}
                alt="QR preview"
                sx={{
                  width: "100%",
                  maxHeight: 220,
                  objectFit: "contain",
                  borderRadius: 2,
                  bgcolor: "#fff",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialog(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            loading={saving}
            disabled={saving || !form.label?.trim()}
            onClick={save}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>Remove QR code?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            "{confirmDelete?.label}" will no longer be shown to users. If any
            collections were paid to it, it is deactivated rather than deleted so
            the history stays intact.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={doDelete}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* ================================================================== */
/*  POS MACHINES                                                       */
/* ================================================================== */

function PosMachinesTab({ notify }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    adminListPosMachines()
      .then(({ data }) => setRows(data.machines || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setForm({ label: "", terminalId: "", note: "" });
    setDialog({});
  };

  const openEdit = (row) => {
    setForm({
      label: row.label,
      terminalId: row.terminal_id || "",
      note: row.note || "",
    });
    setDialog(row);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (dialog && !dialog.id) await adminCreatePosMachine(form);
      else await adminUpdatePosMachine(dialog.id, form);
      setDialog(null);
      notify("success", "Saved.");
      load();
    } catch (e) {
      notify("error", e.response?.data?.error || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await adminUpdatePosMachine(row.id, { isActive: !row.is_active });
      load();
    } catch (e) {
      notify("error", e.response?.data?.error || "Could not update");
    }
  };

  const doDelete = async () => {
    try {
      const { data } = await adminDeletePosMachine(confirmDelete.id);
      notify(
        "success",
        data.deactivated
          ? "This machine has collections against it, so it was deactivated instead of deleted."
          : "Machine removed.",
      );
      setConfirmDelete(null);
      load();
    } catch (e) {
      notify("error", e.response?.data?.error || "Could not remove");
    }
  };

  if (loading)
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" color="text.secondary">
          Register each Pinelabs terminal, then assign it to its users from{" "}
          <b>Users → user → POS machines</b>. The label is what shows on every
          collection from that machine.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddRoundedIcon />}
          onClick={openNew}
          sx={{ borderRadius: 2, whiteSpace: "nowrap" }}
        >
          Add machine
        </Button>
      </Stack>

      <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
        {rows.length === 0 ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <PointOfSaleRoundedIcon
              sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
            />
            <Typography color="text.secondary">
              No POS machines registered yet
            </Typography>
          </Stack>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Label</TableCell>
                  <TableCell>Terminal ID</TableCell>
                  <TableCell>Note</TableCell>
                  <TableCell align="center">Assigned to</TableCell>
                  <TableCell align="center">Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{r.label}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                      {r.terminal_id || "—"}
                    </TableCell>
                    <TableCell
                      sx={{ color: "text.secondary", fontSize: 13, maxWidth: 240 }}
                    >
                      {r.note || "—"}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={`${r.assignedUsers} user${r.assignedUsers === 1 ? "" : "s"}`}
                        variant="outlined"
                        sx={{ height: 22, fontSize: 11 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={!!r.is_active}
                        onChange={() => toggleActive(r)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit(r)}>
                        <EditRoundedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setConfirmDelete(r)}
                      >
                        <DeleteRoundedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Card>

      <Dialog open={!!dialog} onClose={() => !saving && setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>
          {dialog?.id ? "Edit POS machine" : "Add POS machine"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Label"
              placeholder="e.g. Shop 1 counter"
              value={form.label || ""}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              helperText="Shown on every collection from this machine"
            />
            <TextField
              fullWidth
              size="small"
              label="Terminal ID (optional)"
              placeholder="e.g. 7X41002"
              value={form.terminalId || ""}
              onChange={(e) => setForm({ ...form, terminalId: e.target.value })}
            />
            <TextField
              fullWidth
              size="small"
              label="Note (optional)"
              multiline
              minRows={2}
              value={form.note || ""}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialog(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            loading={saving}
            disabled={saving || !form.label?.trim()}
            onClick={save}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>Remove POS machine?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            "{confirmDelete?.label}" will be unassigned from all users. If any
            collections came from it, it is deactivated rather than deleted so the
            history stays intact.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={doDelete}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* ================================================================== */

export default function AdminPaymentPoints() {
  const [tab, setTab] = useState(0);
  const [feedback, setFeedback] = useState(null);

  const notify = (severity, msg) => setFeedback({ severity, msg });

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
        QR Codes &amp; POS Machines
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Where users pay you offline. Payments made here are claimed with a UTR
        and approved under <b>POS &amp; QR Approvals</b>.
      </Typography>

      {feedback && (
        <Alert
          severity={feedback.severity}
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setFeedback(null)}
        >
          {feedback.msg}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab
          icon={<QrCode2RoundedIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="QR Codes"
          sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
        />
        <Tab
          icon={<PointOfSaleRoundedIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="POS Machines"
          sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
        />
      </Tabs>

      {tab === 0 ? <QrCodesTab notify={notify} /> : <PosMachinesTab notify={notify} />}
    </Box>
  );
}
